'use client';

import { useCallback, useEffect, useState } from 'react';
import { cn } from '../utils/cn';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fadeDuration?: number;
}

const loadedImageSrcCache = new Set<string>();

/**
 * Native <img> with lazy loading + smooth fade-in on load.
 * Use this for images that don't need Next.js optimization (banners, external URLs).
 */
export function LazyImage({
  className,
  fadeDuration = 300,
  loading = 'lazy',
  style,
  onLoad,
  onError,
  src,
  ...props
}: LazyImageProps) {
  const srcKey = typeof src === 'string' ? src : null;
  const [isLoaded, setIsLoaded] = useState(() => (srcKey ? loadedImageSrcCache.has(srcKey) : false));

  useEffect(() => {
    setIsLoaded(srcKey ? loadedImageSrcCache.has(srcKey) : false);
  }, [srcKey]);

  const handleRef = useCallback((node: HTMLImageElement | null) => {
    if (!node || !srcKey) return;
    if (!node.complete || node.naturalWidth === 0) return;

    loadedImageSrcCache.add(srcKey);
    setIsLoaded(true);
  }, [srcKey]);

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      ref={handleRef}
      src={src}
      loading={loading}
      decoding="async"
      onLoad={(e) => {
        if (srcKey) loadedImageSrcCache.add(srcKey);
        setIsLoaded(true);
        onLoad?.(e);
      }}
      onError={onError}
      className={cn(
        'transition-opacity',
        isLoaded ? 'opacity-100' : 'opacity-0',
        className
      )}
      style={{ transitionDuration: `${fadeDuration}ms`, ...style }}
      {...props}
    />
  );
}
